import React, {Component} from 'react';
import {computed} from 'mobx';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import PropTypes from 'prop-types';

import {ALA_CARTE_CREDITS, MEDIA_STICK_CREDITS} from '../../../../../constants/pricingConstants';

import inject from '../../../../hoc/injectHoc';
import {PAYMENT_PLAN_ANNUAL_ANNUAL, PAYMENT_PLAN_ANNUAL_MONTHLY, PAYMENT_PLAN_MONTHLY} from '../../../../../constants/subscriptionPlanConstants';

import LoadingIcon from '../../../../common/loadingIcon/LoadingIcon';
import {centsToDollars} from '../../../../../utils/mathHelper';
import './orderDetails.scss';

const TEN_PERCENT = 0.1;
const FIVE_PERCENT = 0.05;
const ONE_YEAR_MONTHS = 12;

/**
 * The OrderDetails component.
 */
class OrderDetails extends Component {
  /**
   * Calculate discounts for year commitments
   * 10% for a year plan paid upfront
   * 5% for a year contract paid monthly
   *
   * @param {number} price
   * @returns {number}
   */
  annualDiscount(price) {
    switch (this.props.billingType) {
      case PAYMENT_PLAN_ANNUAL_ANNUAL:
        return price * (1 - TEN_PERCENT);
      case PAYMENT_PLAN_ANNUAL_MONTHLY:
        return price * (1 - FIVE_PERCENT);
      default:
        return price;
    }
  }

  /**
   * Calculates the plan price per month
   */
  @computed get planPrice() {
    const {plan} = this.props;

    const price = this.annualDiscount(plan.priceCents);
    return centsToDollars(price);
  }

  /**
   * Calculate additional sign count
   */
  @computed get additionalSignCount() {
    const {userSigns} = this.props;
    const signCount = userSigns ? userSigns.length : 0;

    return (signCount > 0) ? (signCount - 1) : 0;
  }

  /**
   * Calculate and format the price of sign displays
   */
  @computed get displayPrice() {
    const {plan} = this.props;

    const price = plan.displayPriceCents;
    const total = this.annualDiscount(price);

    return centsToDollars(total);
  }

  /**
   * Calculate and format total purchase amount
   */
  @computed get totalRecurringPurchases() {
    const {plan, mediaStickPlan} = this.props;
    let price = 0;

    if (plan) {
      price += this.annualDiscount(plan.priceCents + (this.additionalSignCount * plan.displayPriceCents));
    }

    if (mediaStickPlan && mediaStickPlan.frequency === 'monthly') {
      price += mediaStickPlan.quantity * MEDIA_STICK_CREDITS[mediaStickPlan.frequency].priceCents;
    }

    return price > 0 ? centsToDollars(price) : 0;
  }

  /**
   * Calculate and format total purchase amount
   */
  @computed get totalOneTimePurchases() {
    const {mediaStickPlan, alaCarteCredits} = this.props;

    let price = 0;
    if (mediaStickPlan && mediaStickPlan.frequency === 'lifetime') {
      price += mediaStickPlan.quantity * MEDIA_STICK_CREDITS[mediaStickPlan.frequency].priceCents;
    }

    if (alaCarteCredits) {
      price += alaCarteCredits.download > 0 && (alaCarteCredits.download * ALA_CARTE_CREDITS.download.pricePerCredit);
      price += alaCarteCredits.request > 0 && (alaCarteCredits.request * ALA_CARTE_CREDITS.request.pricePerCredit);
    }

    return price > 0 ? centsToDollars(price) : 0;
  }

  /**
   * Calculate total billed amount for today
   */
  @computed get totalToday() {
    const {plan, billingType, mediaStickPlan, alaCarteCredits} = this.props;
    let price = 0;

    if (plan) {
      price += this.annualDiscount(plan.priceCents + (this.additionalSignCount * plan.displayPriceCents));
    }
    if (plan && billingType === PAYMENT_PLAN_ANNUAL_ANNUAL) {
      price *= ONE_YEAR_MONTHS;
    }

    if (mediaStickPlan) {
      price += mediaStickPlan.quantity * MEDIA_STICK_CREDITS[mediaStickPlan.frequency].priceCents;
    }

    if (alaCarteCredits) {
      price += alaCarteCredits.download > 0 && (alaCarteCredits.download * ALA_CARTE_CREDITS.download.pricePerCredit);
      price += alaCarteCredits.request > 0 && (alaCarteCredits.request * ALA_CARTE_CREDITS.request.pricePerCredit);
    }

    return centsToDollars(price);
  }

  /**
  * Input onChange handler
  *
  * @param {string} billingType
  */
  onChange = (billingType) => {
    this.props.onChangeBillingType(billingType);
  }

  /**
   * Renders the radio buttons for plan billing type options
   * @returns {{}}
   */
  renderPlanBillingTypes() {
    const billingTypes = [
      // can only use this feature with an upgraded Chargebee plan
      {
        type: PAYMENT_PLAN_ANNUAL_ANNUAL,
        text: 'Save 10% on plan billed Yearly'
      },
      {
        type: PAYMENT_PLAN_ANNUAL_MONTHLY,
        text: 'Save 5% on plan billed Monthly for 12 months'
      },
      {
        type: PAYMENT_PLAN_MONTHLY,
        text: 'Month to Month'
      }
    ];

    return billingTypes.map((item) => {
      return (
        <div key={item.type}>
          <label className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="billingType"
              value={item.type}
              checked={item.type === this.props.billingType}
              onChange={() => this.onChange(item.type)}
            />
            <span>{item.text}</span>
          </label>
        </div>
      );
    });
  }

  /**
   * Renders the plan line item and additional sign line items
   * @returns {{}}
   */
  renderOrderDetails() {
    const {
      plan,
      billingType,
    } = this.props;

    if (!plan) {
      return <></>;
    }

    return (
      <>
        {(plan.priceCents > 0) && (this.renderPlanBillingTypes())}
        <div className="d-flex justify-content-between">
          <div>
            <div className="selected-plan">{plan.name} Plan</div>
            <div>{billingType === PAYMENT_PLAN_ANNUAL_ANNUAL ? '' : '(recurring monthly payment)'}</div>
          </div>
          <div>
            <div className={billingType !== PAYMENT_PLAN_MONTHLY ? 'cost-markout' : ''}>{plan.price}/mo</div>
            {(billingType !== PAYMENT_PLAN_MONTHLY) && (<div>{this.planPrice}/mo</div>)}
          </div>
        </div>
        {(this.additionalSignCount > 0) && (
          <div className="d-flex justify-content-between">
            <div>
              <div className="selected-plan">Additional Displays ({this.additionalSignCount})</div>
              <div>(recurring monthly payment)</div>
            </div>
            <div>{this.displayPrice}/sign/mo</div>
          </div>
        )}
      </>
    );
  }

  /**
   * Renders the media stick line items
   * @returns {{}}
   */
  renderMediaStickOrderDetails() {
    const {mediaStickPlan} = this.props;

    if (!mediaStickPlan) {
      return <></>;
    }
    const pricePerInDollars = centsToDollars(MEDIA_STICK_CREDITS[mediaStickPlan.frequency].priceCents);

    return (
      <>
        <div className="d-flex justify-content-between pt-3">
          <div>
            <div className="selected-plan">Media Stick x {mediaStickPlan.quantity}</div>
            {(mediaStickPlan.frequency === 'monthly' && <div>(recurring monthly payment)</div>)}
          </div>
          <div>
            <div>{pricePerInDollars}{mediaStickPlan.frequency === 'monthly' ? '/mo' : ' one time'}</div>
          </div>
        </div>
      </>
    );
  }

  /**
   * Renders the ala carte line items
   * @returns {{}}
   */
  renderAlaCarteOrderDetails() {
    const {alaCarteCredits} = this.props;

    if (!alaCarteCredits) {
      return <></>;
    }
    const pricePerDownloadCreditInDollars = centsToDollars(ALA_CARTE_CREDITS.download.pricePerCredit);
    const pricePerRequestCreditInDollars = centsToDollars(ALA_CARTE_CREDITS.request.pricePerCredit);

    return (
      <>
        {alaCarteCredits.download > 0 && (<div className="d-flex justify-content-between pt-3">
          <div>
            <div className="selected-plan">
              Download Credit{alaCarteCredits.download > 1 ? 's' : ''} x {alaCarteCredits.download}
            </div>
          </div>
          <div>
            {pricePerDownloadCreditInDollars}
          </div>
        </div>)}
        {alaCarteCredits.request > 0 && (<div className="d-flex justify-content-between">
          <div>
            <div className="selected-plan">
              Custom Credit{alaCarteCredits.request > 1 ? 's' : ''} x {alaCarteCredits.request}
            </div>
          </div>
          <div>
            {pricePerRequestCreditInDollars}
          </div>
        </div>)}
      </>
    );
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {
      buttonText,
      isLoading,
      onSubmit,
      billingType,
    } = this.props;

    return (
      <div
        className="order-details col-12 col-md-3 ml-md-5"
        data-cy="order-details-page"
      >
        <div className="order-details-top">
          <div className="font-weight-bold text-uppercase">Order details</div>
          {this.renderOrderDetails()}
          {this.renderMediaStickOrderDetails()}
          {this.renderAlaCarteOrderDetails()}
        </div>
        <div className="order-details-middle">
          <div className="d-flex justify-content-between">
            <div>Total Purchases</div>
            <div>
              {this.totalRecurringPurchases !== 0 && (<div>{this.totalRecurringPurchases}/mo {billingType === PAYMENT_PLAN_ANNUAL_ANNUAL ? 'x 12' : ''}</div>)}
              {this.totalOneTimePurchases !== 0 && <div>{this.totalOneTimePurchases} one time</div>}
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <div>Estimated Tax</div>
            <div>$ 0</div>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div className="font-weight-bold">Total Today</div>
            <div className="price">{this.totalToday}</div>
          </div>
        </div>
        <div className="order-details-bottom">
          <div className="d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-primary text-light"
              onClick={onSubmit}
              disabled={isLoading}
              data-cy="confirm-purchase-button"
            >
              {isLoading ? <span className="d-flex"><LoadingIcon size="em" /> {buttonText}</span> : buttonText}
            </button>
          </div>
        </div>
        {this.totalToday !== 'FREE' && <div className="disclaimer">*Once your purchase is complete, a receipt will be emailed to you.</div>}
      </div>
    );
  }
}

OrderDetails.propTypes = {
  billingType: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onboardingStore: MobxPropTypes.observableObject.isRequired,
  alaCarteCredits: PropTypes.shape({
    download: PropTypes.number,
    request: PropTypes.number,
  }),
  mediaStickPlan: PropTypes.shape({
    frequency: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired
  }),
  onChangeBillingType: PropTypes.func,
  plan: PropTypes.shape({
    displayPrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]).isRequired,
    displayPriceCents: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    priceCents: PropTypes.number.isRequired
  }),
  userSigns: PropTypes.object
};

OrderDetails.wrappedComponent = {};
OrderDetails.wrappedComponent.propTypes = {
  onboardingStore: MobxPropTypes.observableObject.isRequired
};

export default inject(OrderDetails)(
  observer(OrderDetails)
);
